import { forwardRef } from 'react';
import type { ComponentShadow } from 'src/modules/shared/types/component.type';
import type { TextInputProps } from 'src/modules/shared/types/text-input.types';
import {
  baseInputClasses,
  inputVariantClasses,
  inputSizeClasses,
  getDisabledClasses,
  getErrorClasses,
} from 'src/modules/shared/styles/component.styles';
import InputError from '../input-error';

const TextInput = forwardRef<HTMLInputElement, TextInputProps>(
  (
    {
      className = '',
      variant = 'primary',
      size = 'md',
      disabled = false,
      error,
      shadow,
      leadingIcon,
      ...rest
    },
    ref
  ) => {
    const shadowMap: Record<ComponentShadow, string> = {
      'inset-sm': 'shadow-sm',
      inset: 'shadow-md',
      flat: 'shadow-md',
      none: '',
      sm: 'shadow-sm',
      md: 'shadow-md',
      lg: 'shadow-lg',
      xl: 'shadow-xl',
      '2xl': 'shadow-2xl',
      inner: 'shadow-inner',
    };
    const shadowClasses = shadow ? `${shadowMap[shadow]}` : '';

    const classes = [
      ...baseInputClasses,
      inputVariantClasses[variant],
      inputSizeClasses[size],
      getDisabledClasses(disabled),
      getErrorClasses(!!error),
      'border',
      shadowClasses,
      leadingIcon && '!pl-10',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    const input = (
      <input
        ref={ref}
        type="text"
        className={classes}
        disabled={disabled}
        {...rest}
      />
    );

    return (
      <div className="flex flex-1 flex-col w-full">
        {leadingIcon ? (
          <div className="relative w-full">
            <span
              aria-hidden
              className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-secondary-500"
            >
              {leadingIcon}
            </span>
            {input}
          </div>
        ) : (
          input
        )}
        <InputError message={error} />
      </div>
    );
  }
);

TextInput.displayName = 'TextInput';

export default TextInput;
